import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail } from "lucide-react";

export default function AdminEmails() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);

  useEffect(() => { api.get("/admin/email-logs").then((r) => setList(r.data)); }, []);

  return (
    <div data-testid="admin-emails">
      <h1 className="font-serif text-3xl text-[#001F3F]">Email Queue</h1>
      <div className="w-10 h-[2px] bg-[#2BA6A6] mt-3 mb-6"></div>
      <p className="text-sm font-sans text-gray-600 mb-6 max-w-2xl">
        All outgoing emails (submission confirmations, decision notices, admin alerts, contact form messages)
        are queued and stored here. They are also visible to the editor for record-keeping.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200">
          <div className="bg-[#F5F7FA] px-4 py-3 text-[11px] uppercase tracking-wider text-[#001F3F] font-semibold">All Emails ({list.length})</div>
          <div className="max-h-[600px] overflow-y-auto">
            {list.length === 0 ? <div className="p-8 text-center text-gray-500 text-sm">No emails yet.</div> :
              list.map((e) => (
                <button key={e.id} onClick={() => setSel(e)} className="w-full text-left px-4 py-3 border-t border-gray-100 hover:bg-[#F5F7FA] flex items-start gap-3" data-testid={`email-${e.id}`}>
                  <Mail className="w-4 h-4 text-[#2BA6A6] mt-1 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-sm text-[#001F3F] font-serif truncate flex-1">{e.subject}</div>
                      <StatusPill status={e.status} />
                    </div>
                    <div className="text-xs text-gray-500 truncate">→ {e.to_email}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">{e.created_at?.slice(0, 19).replace("T", " ")} · {e.purpose}{e.manuscript_id ? ` · ${e.manuscript_id}` : ""}</div>
                  </div>
                </button>
              ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-5">
          {sel ? (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#2BA6A6]">To</div>
                  <div className="text-sm text-[#001F3F]">{sel.to_email}</div>
                </div>
                <StatusPill status={sel.status} />
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.25em] font-semibold text-[#2BA6A6]">Subject</div>
              <div className="text-base font-serif text-[#001F3F]">{sel.subject}</div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div><div className="text-[10px] uppercase tracking-wider text-gray-500">Manuscript ID</div><div className="text-sm text-[#001F3F]">{sel.manuscript_id || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-gray-500">Resend Message ID</div><div className="text-xs font-mono text-[#001F3F] break-all">{sel.message_id || "—"}</div></div>
              </div>
              {sel.status === "failed" && (
                <div className="mt-3 bg-red-50 border border-red-200 text-red-700 text-xs p-3" data-testid="email-error">
                  <span className="font-semibold uppercase tracking-wider">Error:</span> {sel.error || "Unknown error"}
                </div>
              )}
              <div className="mt-4 text-[10px] uppercase tracking-[0.25em] font-semibold text-[#2BA6A6]">Body</div>
              <pre className="bg-[#F5F7FA] border border-gray-200 p-4 mt-1 text-xs font-mono whitespace-pre-wrap text-[#001F3F]">{sel.body}</pre>
              <div className="mt-4 flex gap-3 text-xs">
                <a href={`mailto:${sel.to_email}?subject=${encodeURIComponent(sel.subject)}&body=${encodeURIComponent(sel.body)}`} className="bg-[#2BA6A6] text-white px-4 py-2 uppercase tracking-wider hover:bg-[#001F3F]" data-testid="open-in-mail">Open in Mail Client</a>
              </div>
            </>
          ) : <div className="text-center text-gray-500 py-12 text-sm">Select an email to view</div>}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
    failed: "bg-red-50 text-red-700 border-red-200",
    queued: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 border shrink-0 ${map[status] || "bg-gray-50 text-gray-600 border-gray-200"}`} data-testid="email-status">{status || "—"}</span>;
}
